import { useEffect, useMemo, type CSSProperties, type ReactElement } from "react";
import type { ProgressState } from "@rietz/shared";
import { uiActions } from "../actions.js";
import type { LeaderboardRow } from "../store.js";
import { UI_FAMILY } from "../theme.js";
import { formatCount } from "./format.js";
import { Badge, Bar, Card, CardTitle, KV, Note, Num, Stat, Title } from "./kit.js";
import {
  boardViews,
  progressViews,
  TOP_LEVEL,
  type SkillView,
} from "./profileLogic.js";

/**
 * WHO THE PLAYER IS: level, the four skills, the lifetime tally, the wall of
 * names. The one tab with NO VERB — a readout; everything these numbers GATE is
 * drawn beside the thing it gates (`offerLogic.ts`, the quest board). Driven by
 * props; the one thing it reaches for itself is the leaderboard, through
 * `uiActions`.
 */

/** Stable identity for the "no board yet" case, so the memo below is not
 *  rebuilt on every render by a fresh empty array. */
const NO_ROWS: readonly LeaderboardRow[] = Object.freeze([]);

/** Cards keep their height; the column scrolls, they do not squash. */
const CARD: CSSProperties = { flex: "0 0 auto" };

/** The monogram, cut from the same cloth as the top bar's `.mm-avatar`. */
const AVATAR: CSSProperties = {
  flex: "0 0 auto",
  display: "grid",
  placeItems: "center",
  width: 48,
  height: 48,
  borderRadius: 12,
  background: "linear-gradient(160deg, rgba(96, 124, 156, 0.7), rgba(56, 70, 90, 0.7))",
  font: `700 20px/1 ${UI_FAMILY}`,
  textTransform: "uppercase",
};

/** The lit ladder row. `data-me` colours it through `.mm-table`; the inline
 *  copy is what a render with no stylesheet still shows. */
const ME_ROW: CSSProperties = { color: "#f0d39c" };

export interface ProfileTabProps {
  /** The display name, straight off the profile. Neither unique nor stable —
   *  which is exactly why the board below can only guess at our row. */
  name: string;
  progress: ProgressState;
  /** The ladder as last fetched, or `null` while nobody has an answer yet. */
  leaderboard: LeaderboardRow[] | null;
}

export function ProfileTab(props: ProfileTabProps): ReactElement {
  const views = useMemo(() => progressViews(props.progress), [props.progress]);

  /**
   * ASKED FOR ONCE, WHEN THE TAB APPEARS: a board changes at the speed of other
   * people's raids, and opening the tab is the one moment a player asked. Empty
   * deps on purpose; the answer lands in the store.
   */
  useEffect(() => {
    uiActions().fetchLeaderboard();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 980,
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <Card style={CARD}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={AVATAR} aria-hidden="true">
            {props.name.slice(0, 1)}
          </span>
          <Title size="lg">{props.name}</Title>
          <Badge>LEVEL {views.xp.level}</Badge>
          <span style={{ flex: 1 }} />
          <Num tone="dim">
            {views.xp.atMax ? `Level ${TOP_LEVEL} of ${TOP_LEVEL}` : views.xp.totalLabel}
          </Num>
        </div>
        <Bar fill01={views.xp.fill01} done={views.xp.atMax} />
        <KV label={views.xp.atMax ? "Nothing left to climb." : `Next: level ${views.xp.level + 1}`}>
          <Num tone={views.xp.atMax ? "good" : "accent"}>{views.xp.label}</Num>
        </KV>
      </Card>

      <Card style={CARD}>
        <CardTitle>SKILLS</CardTitle>
        {/* Nobody spends a point here and there is nothing to press: each bar
            says what it is worth and what raises it, and the raid does the
            rest. See `shared/meta/progress.ts`. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 12,
          }}
        >
          {views.skills.map((skill) => (
            <SkillBlock key={skill.id} skill={skill} />
          ))}
        </div>
      </Card>

      <Card style={CARD}>
        <CardTitle>LIFETIME</CardTitle>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
          }}
        >
          {views.stats.map((stat) => (
            <Stat key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </div>
      </Card>

      <Card style={CARD}>
        <CardTitle aside={props.leaderboard === null ? undefined : props.leaderboard.length}>
          LEADERBOARD
        </CardTitle>
        <Board rows={props.leaderboard} myName={props.name} />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SkillBlock(props: { skill: SkillView }): ReactElement {
  const { skill } = props;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontWeight: 500 }}>{skill.label}</span>
        <Num tone="dim">{skill.valueLabel} / 100</Num>
      </div>
      <Bar fill01={skill.fill01} done={skill.fill01 >= 1} thin />
      <KV label={<Note>{skill.raisedBy}</Note>}>
        <Num tone="accent" style={{ fontSize: 11.5 }}>
          {skill.effect}
        </Num>
      </KV>
    </div>
  );
}

/**
 * The wall of names. THREE STATES AND NO ERROR ONE: not arrived, empty and
 * failed all read the same — none is something the player can do anything
 * about.
 */
function Board(props: { rows: LeaderboardRow[] | null; myName: string }): ReactElement {
  const rows = useMemo(
    () => boardViews(props.rows ?? NO_ROWS, props.myName),
    [props.rows, props.myName],
  );

  if (rows.length === 0) {
    return <Note>{props.rows === null ? "Asking the server…" : "Nobody has come back yet."}</Note>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="mm-table">
        <thead>
          <tr>
            <th scope="col" className="mm-num">
              #
            </th>
            <th scope="col">Name</th>
            <th scope="col" className="mm-num">
              Level
            </th>
            <th scope="col" className="mm-num">
              XP
            </th>
            <th scope="col" className="mm-num">
              Raids
            </th>
            <th scope="col" className="mm-num">
              Extracted
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((view) => (
            <tr
              key={`${view.rank}:${view.row.name}`}
              data-me={view.me ? "true" : undefined}
              style={view.me ? ME_ROW : undefined}
            >
              <td className="mm-num">{view.rank}</td>
              <td>{view.row.name}</td>
              <td className="mm-num">{view.row.level}</td>
              <td className="mm-num">{formatCount(view.row.xp)}</td>
              <td className="mm-num">{formatCount(view.row.raids)}</td>
              <td className="mm-num">{formatCount(view.row.extracts)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
